import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ImageUp, PawPrint, Sparkles, User, Users, X } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg, inr } from "@/lib/api";

const MAX = 10;
const parseTraits = (text) => {
  const out = [];
  for (const raw of text.split(/[\n,;]/)) {
    const v = raw.replace(/^[-•*\d.)\s]+/, "").trim();
    if (v && !out.some((x) => x.toLowerCase() === v.toLowerCase())) out.push(v);
  }
  return out;
};
export const SUBJECTS = {
  single: { label: "Single", icon: User, max: 1, who: "one person", nameLabel: "Their name", namePh: "e.g. Priya", photoHint: "Face clearly visible · one person",
    traitLabel: "What are they like?", ideas: ["Loves cats", "Gym every morning", "Game of Thrones fan", "Chai addict", "Cricket crazy", "Bollywood buff", "Always on a road trip", "Bakes on weekends", "Can't stop gardening", "Plays guitar", "Biryani connoisseur", "Marathon runner"] },
  couple: { label: "Couple", icon: Heart, max: 2, who: "two people", nameLabel: "Their names", namePh: "e.g. Ananya & Rohan", photoHint: "One photo of both, or one each · faces clear",
    traitLabel: "What are they like together?", ideas: ["Met over filter coffee", "He cooks, she critiques", "Binge-watch everything", "Trek every monsoon", "She's the planner", "He's the DJ on road trips", "Both chai addicts", "Argue about pineapple on pizza", "Garba champions", "Cat parents", "Sunday brunch ritual", "Cricket vs. Bollywood nights"] },
  family: { label: "Family", icon: Users, max: 4, who: "the whole family", nameLabel: "Family name", namePh: "e.g. The Sharmas", photoHint: "Up to 4 photos · everyone (and pets!) visible",
    traitLabel: "What's your family like?", ideas: ["Golden retriever named Bruno", "Two cats who run the house", "Board-game Sundays", "Dad grills, Mom judges", "Kids obsessed with cricket", "Annual Goa trip", "Nani's laddoos", "Loud antakshari nights", "Everyone's a foodie", "Garden full of tulsi", "Diwali card parties", "Three generations of chai"] },
  pet: { label: "Just my pet", icon: PawPrint, max: 2, who: "your pet", nameLabel: "Pet's name", namePh: "e.g. Bruno", photoHint: "Up to 2 photos · face and full body if you can",
    traitLabel: "What's your pet like?", ideas: ["Steals socks", "Terrified of the vacuum", "Thinks it's a lap dog", "Judges everyone from the windowsill", "Lives for paneer scraps", "Chases pigeons, never wins", "Snores louder than Dad", "Zoomies at 11 pm", "Guards the house from autos", "Demands belly rubs", "Head-tilts at 'walk'", "Sleeps in the laundry basket"] },
};

export const STYLES = [
  ["mono", "Modern mono", "Black & white ink with pops of one colour", "bg-[#26232E]"],
  ["bollywood", "Bollywood poster", "Hand-painted, dramatic, saturated", "bg-gradient-to-br from-[#D64545] via-[#E5B063] to-[#1F7A8C]"],
  ["comic", "Comic-book", "Bold inks, halftones, speech bubbles", "bg-gradient-to-br from-[#3B5BDB] to-[#EC4899]"],
];

export default function CaricatureNew() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [subject, setSubject] = useState("single");
  const [style, setStyle] = useState("mono");
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [about, setAbout] = useState("");
  const [busy, setBusy] = useState(false);
  const S = SUBJECTS[subject];
  const traits = parseTraits(about);

  const pick = (list) => {
    const imgs = Array.from(list || []).filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return toast.error("Please choose photos (JPG, PNG or HEIC)");
    const next = [...files, ...imgs.map((f) => ({ f, url: URL.createObjectURL(f) }))].slice(0, S.max);
    if (files.length + imgs.length > S.max) toast.warning(`Up to ${S.max} photo${S.max > 1 ? "s" : ""} for a ${S.label.toLowerCase()} caricature`);
    setFiles(next);
  };
  const changeSubject = (k) => { setSubject(k); setFiles((fs) => fs.slice(0, SUBJECTS[k].max)); };
  const addIdea = (t) => {
    if (traits.length >= MAX) return toast.warning("Ten things is plenty — the artist needs room to draw!");
    setAbout((a) => (a.trim() ? `${a.replace(/\s+$/, "")}\n${t}` : t));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error(`Add a clear photo of ${S.who}`);
    const all = traits.slice(0, MAX);
    if (!all.length) return toast.error("Tell us at least one thing about them");
    setBusy(true);
    const fd = new FormData();
    files.forEach(({ f }) => fd.append("files", f));
    fd.append("name", name);
    fd.append("subject", subject);
    fd.append("style", style);
    fd.append("traits", JSON.stringify(all));
    try {
      const { data } = await api.post("/caricatures", fd);
      nav(`/caricature/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto grid max-w-6xl gap-12 px-5 py-12 sm:px-8 lg:grid-cols-[380px_1fr]">
        <div>
          <p className="overline">Caricature poster</p>
          <h1 className="mt-2 font-serif text-4xl tracking-tight sm:text-5xl">Turn them into a poster they'll never live down.</h1>
          <p className="mt-5 text-sm leading-relaxed text-[#5E5868]">A photo, up to ten things that make them <em>them</em>. Our artist draws a caricature — big heads, tiny bodies, and all their quirks in the frame — in modern monochrome with pops of colour, Bollywood-poster or comic-book style. Singles, couples, whole families — or just your pet.</p>
          <ul className="mt-6 space-y-2 text-sm text-[#5E5868]">
            <li className="flex justify-between border-b border-[#E8E2D9] pb-2"><span>Digital poster (high-res download)</span><strong className="text-[#26232E]">{inr(399)}</strong></li>
            <li className="flex justify-between border-b border-[#E8E2D9] pb-2"><span>Printed A3 poster, delivered</span><strong className="text-[#26232E]">{inr(1000)}</strong></li>
          </ul>
          <p className="mt-5 rounded-lg border border-[#E8E2D9] bg-[#F5F0E8]/60 p-3 text-xs text-[#5E5868]" data-testid="caricature-one-shot-note">
            <strong className="text-[#26232E]">One shot, one portrait.</strong> The artist draws once — so pick sharp, well-lit, front-facing photos.
          </p>
        </div>

        <form onSubmit={submit} className="paper rounded-lg p-6 sm:p-8" data-testid="caricature-form">
          <Label className="text-sm font-semibold">Who's in it?</Label>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup" data-testid="caricature-subject-group">
            {Object.entries(SUBJECTS).map(([k, v]) => (
              <button key={k} type="button" role="radio" aria-checked={subject === k} data-testid={`caricature-subject-${k}`} onClick={() => changeSubject(k)}
                className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm transition-colors ${subject === k ? "border-[#3B5BDB] bg-[#3B5BDB]/5 text-[#26232E]" : "border-[#E8E2D9] bg-white text-[#5E5868] hover:border-[#3B5BDB]/60"}`}>
                <v.icon className={`h-5 w-5 ${subject === k ? "text-[#3B5BDB]" : ""}`} strokeWidth={1.6} />
                <span className="font-medium">{v.label}</span>
              </button>
            ))}
          </div>

          <Label className="mt-6 block text-sm font-semibold">Art style</Label>
          <div className="mt-2 grid gap-2 sm:grid-cols-3" role="radiogroup" data-testid="caricature-style-group">
            {STYLES.map(([k, label, desc, swatch]) => (
              <button key={k} type="button" role="radio" aria-checked={style === k} data-testid={`caricature-style-${k}`} onClick={() => setStyle(k)}
                className={`flex items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors ${style === k ? "border-[#3B5BDB] bg-[#3B5BDB]/5" : "border-[#E8E2D9] bg-white hover:border-[#3B5BDB]/60"}`}>
                <span className={`h-9 w-9 shrink-0 rounded-md ${swatch}`} />
                <span><span className="block font-medium text-[#26232E]">{label}</span><span className="block text-xs text-[#5E5868]">{desc}</span></span>
              </button>
            ))}
          </div>

          <Label className="mt-6 block text-sm font-semibold">{S.max > 1 ? "Their photos" : "Their photo"}</Label>
          <div
            data-testid="caricature-dropzone"
            onClick={() => files.length < S.max && fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files); }}
            className={`mt-2 flex flex-wrap items-center gap-4 rounded-lg border-2 border-dashed border-[#E8E2D9] p-4 transition-colors ${files.length < S.max ? "cursor-pointer hover:border-[#3B5BDB]" : ""}`}
          >
            {files.map(({ f, url }, i) => (
              <div key={url} className="relative">
                <img src={url} alt={f.name} data-testid="caricature-photo-preview" className="h-24 w-24 rounded-md object-cover" />
                <button type="button" data-testid={`caricature-photo-remove-${i}`} onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, j) => j !== i)); }}
                  className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-[#26232E] text-white shadow" aria-label="Remove photo"><X className="h-3 w-3" /></button>
              </div>
            ))}
            {files.length < S.max && (
              <div className="grid h-24 w-24 place-items-center rounded-md bg-[#F5F0E8] text-[#8C8498]"><ImageUp className="h-7 w-7" strokeWidth={1.4} /></div>
            )}
            <div className="min-w-[140px] flex-1 text-sm">
              <p className="font-medium">{files.length ? `${files.length}/${S.max} photo${S.max > 1 ? "s" : ""} added` : `Drop ${S.max > 1 ? "photos" : "a photo"} or tap to choose`}</p>
              <p className="mt-1 text-xs text-[#8C8498]">{S.photoHint} · JPG, PNG or HEIC</p>
            </div>
            <input ref={fileRef} data-testid="caricature-file-input" type="file" accept="image/*" multiple={S.max > 1} className="hidden" onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
          </div>

          <div className="mt-6 space-y-1.5">
            <Label htmlFor="cname" className="text-sm font-semibold">{S.nameLabel}</Label>
            <Input id="cname" data-testid="caricature-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder={S.namePh} className="h-11 bg-white" />
          </div>

          <div className="mt-6">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="ctrait" className="text-sm font-semibold">{S.traitLabel}</Label>
              <span className={`font-mono text-xs ${traits.length > MAX ? "text-[#C2185B]" : "text-[#8C8498]"}`} data-testid="caricature-trait-count">{Math.min(traits.length, MAX)}/{MAX}</span>
            </div>
            <p className="mt-1 text-xs text-[#8C8498]">Write freely in your own words — hobbies, quirks, inside jokes. One thing per line (or separate with commas). Up to {MAX}.</p>
            <Textarea id="ctrait" data-testid="caricature-trait-input" value={about} onChange={(e) => setAbout(e.target.value)} rows={5}
              placeholder={`e.g.\n${S.ideas[0]}\n${S.ideas[1]}\nAlways late, blames the traffic`} className="mt-2 min-h-[132px] resize-y bg-white text-base leading-relaxed sm:text-sm" />
            {traits.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-2" data-testid="caricature-trait-list" aria-label="What the artist will draw">
                {traits.slice(0, MAX).map((t) => (
                  <li key={t} data-testid="caricature-trait-chip" className="rounded-full bg-[#1E1B24] px-3.5 py-1.5 text-xs font-medium text-[#FAF8F5]">{t}</li>
                ))}
                {traits.length > MAX && <li className="px-1 py-1.5 text-xs text-[#C2185B]" data-testid="caricature-trait-overflow">Only the first {MAX} will be used</li>}
              </ul>
            )}
            <p className="mt-4 text-xs text-[#8C8498]">Stuck? Tap any of these to add them:</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {S.ideas.filter((i) => !traits.some((t) => t.toLowerCase() === i.toLowerCase())).slice(0, 8).map((i) => (
                <button key={i} type="button" data-testid="caricature-idea-chip" onClick={() => addIdea(i)} className="rounded-full border border-[#E8E2D9] bg-white px-3 py-1.5 text-xs text-[#5E5868] transition-colors hover:border-[#3B5BDB] hover:text-[#3B5BDB]">+ {i}</button>
              ))}
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="caricature-submit-btn" className="btn-primary mt-8 w-full sm:w-auto">
            <Sparkles className="h-4 w-4" />{busy ? "Sending to the artist…" : `Draw ${subject === "single" ? "my" : subject === "pet" ? "my pet's" : "our"} caricature`}
          </button>
          <p className="mt-3 text-xs text-[#8C8498]">Free to preview. Pay only if you love it.</p>
        </form>
      </main>
    </div>
  );
}
